import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { v4 as uuidv4 } from "uuid";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, useParams } from 'react-router-dom';
import { FcFilmReel } from "react-icons/fc";

export default function EditMovie() {
  const navigate = useNavigate();
  const auth = getAuth();
  const { movieId } = useParams(); // Get the movie ID from the URL
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true); // For fetching movie data
  const [posterPreview, setPosterPreview] = useState(null);
  const [actorImagePreview, setActorImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    posterImage: null,
    posterUrl: "", // Store the existing poster URL
    youtubeUrl: "",
    title: "",
    releaseYear: "",
    duration: "",
    description: "",
    language: "",
    country: "",
    genre: "",
    contentRating: "",
    trailerUrl: "",
    isFeatured: false,
    status: "",
    resolution: "",
    mainActorImage: null,
    actorImageUrl: "", // Store the existing actor image URL
    mainActorName: "",
    directors: "",
    cast: "",
    writers: "",
    producers: "",
    userRef: "", // To verify ownership
    userEmail: "", // To verify ownership
    views: 0,
    likes: 0
  });

  const {
    youtubeUrl, title, releaseYear, duration, description, language, country,
    genre, contentRating, trailerUrl, isFeatured, status, resolution,
    mainActorName, directors, cast, writers, producers, posterUrl, actorImageUrl
  } = formData;

  // Fetch movie data when the component mounts
  useEffect(() => {
    async function fetchMovieData() {
      try {
        const movieRef = doc(db, "movies", movieId);
        const movieSnap = await getDoc(movieRef);

        if (!movieSnap.exists()) {
          toast.error("Movie not found");
          navigate("/profile");
          return;
        }

        const movieData = movieSnap.data();

        // Verify that the current user is the owner of the movie
        if (movieData.userRef !== auth.currentUser.uid) {
          toast.error("You are not authorized to edit this movie");
          navigate("/profile");
          return;
        }

        // Populate formData with the fetched movie data
        setFormData({
          ...formData,
          ...movieData,
          posterUrl: movieData.posterUrl || "",
          actorImageUrl: movieData.actorImageUrl || "",
          releaseYear: movieData.releaseYear ? movieData.releaseYear.toString() : "", // Convert to string for input
          duration: movieData.duration ? movieData.duration.toString() : "", // Convert to string for input
          views: movieData.views || 0,
          likes: movieData.likes || 0
        });

        // Set image previews
        setPosterPreview(movieData.posterUrl || null);
        setActorImagePreview(movieData.actorImageUrl || null);
      } catch (error) {
        toast.error("Error fetching movie data");
        console.error("Fetch error:", error);
        navigate("/profile");
      } finally {
        setFetching(false);
      }
    }

    fetchMovieData();
  }, [movieId, auth.currentUser.uid, navigate]);

  function onChange(e) {
    if (e.target.files) {
      const file = e.target.files[0];
      const id = e.target.id;
      if (!file.type.includes("image")) {
        toast.error("Please upload an image file");
        return;
      }

      setFormData((prevState) => ({
        ...prevState,
        [id]: file
      }));

      if (id === "posterImage") {
        setPosterPreview(URL.createObjectURL(file));
      } else if (id === "mainActorImage") {
        setActorImagePreview(URL.createObjectURL(file));
      }
    } else if (e.target.type === 'checkbox') {
      setFormData((prevState) => ({
        ...prevState,
        [e.target.id]: e.target.checked
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [e.target.id]: e.target.value
      }));
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    // Validation (same as UploadMovie)
    if (!posterUrl && !formData.posterImage) {
      setLoading(false);
      toast.error("Please upload a poster image");
      return;
    }

    if (!youtubeUrl) {
      setLoading(false);
      toast.error("Please provide a YouTube URL");
      return;
    }

    if (title.length < 2) {
      setLoading(false);
      toast.error("Title should be at least 2 characters");
      return;
    }

    if (!releaseYear || isNaN(releaseYear) || releaseYear < 1900 || releaseYear > new Date().getFullYear()) {
      setLoading(false);
      toast.error("Please enter a valid release year");
      return;
    }

    if (!duration || isNaN(duration) || duration <= 0) {
      setLoading(false);
      toast.error("Please enter a valid duration in minutes");
      return;
    }

    if (!genre) {
      setLoading(false);
      toast.error("Please select a genre");
      return;
    }

    if (!status) {
      setLoading(false);
      toast.error("Please select a status");
      return;
    }

    async function storeImage(image, folder) {
      return new Promise((resolve, reject) => {
        const storage = getStorage();
        const filename = `${auth.currentUser.uid}-${image.name}-${uuidv4()}`;
        const storageRef = ref(storage, `${folder}/${filename}`);
        const uploadTask = uploadBytesResumable(storageRef, image);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            reject(error);
          },
          () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              resolve(downloadURL);
            });
          }
        );
      });
    }

    try {
      // Only upload new images if the user selected new ones
      let updatedPosterUrl = posterUrl;
      if (formData.posterImage) {
        updatedPosterUrl = await storeImage(formData.posterImage, "posters");
      }

      let updatedActorImageUrl = actorImageUrl;
      if (formData.mainActorImage) {
        updatedActorImageUrl = await storeImage(formData.mainActorImage, "actors");
      }

      // Prepare the updated data
      const formDataCopy = {
        ...formData,
        posterUrl: updatedPosterUrl,
        actorImageUrl: updatedActorImageUrl,
        timestamp: serverTimestamp(),
        userRef: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        views: formData.views || 0,
        likes: formData.likes || 0
      };

      // Remove temporary file objects from the data
      delete formDataCopy.posterImage;
      delete formDataCopy.mainActorImage;

      // Update the Firestore document
      const movieRef = doc(db, "movies", movieId);
      await updateDoc(movieRef, formDataCopy);

      setLoading(false);
      toast.success("Movie updated successfully!");
      navigate(`/movie/${movieId}`);
    } catch (error) {
      setLoading(false);
      toast.error("Error updating movie. Please try again.");
      console.error("Update error:", error);
    }
  }

  if (fetching) {
    return <Spinner />;
  }

  if (loading) {
    return <Spinner />;
  }

  const genreOptions = [
    "Action", "Horror", "Series", "Adventure", "Love Story", "Comedy", "Crime",
    "Family", "Sci-Fi", "Romance", "Kungu-Fu", "Drama", "Sport", "Thriller",
    "Animation", "Documentaries", "NollyWood", "BollyWood", "Ugandan", "Kenyan", "Luganda-Translated", "Trailers"
  ];

  const statusOptions = ["Trending", "Popular", "Latest"];

  return (
    <main className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-2xl mx-auto bg-gray-900 rounded-xl shadow-2xl overflow-hidden p-8">
        <div className="text-center mb-8">
          <FcFilmReel className="mx-auto text-5xl mb-4" />
          <h1 className="text-3xl font-bold text-[#FF1493]">Edit Movie</h1>
          <p className="text-gray-400 mt-2">Update the movie details</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-8">
          {/* Poster Image Section */}
          <div className="border-2 border-dashed border-[#FF1493] rounded-xl p-4 text-center">
            {posterPreview ? (
              <div className="flex flex-col items-center">
                <img 
                  src={posterPreview} 
                  alt="Poster Preview" 
                  className="max-h-64 mx-auto rounded-lg mb-4"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPosterPreview(null);
                    setFormData(prev => ({ ...prev, posterImage: null }));
                  }}
                  className="text-[#FF1493] hover:text-[#FF8C00] text-sm"
                >
                  Change Poster
                </button>
              </div>
            ) : (
              <div className="py-12">
                <label className="cursor-pointer">
                  <div className="flex flex-col items-center justify-center">
                    <svg
                      className="w-12 h-12 text-[#FF1493] mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="text-lg text-[#FF1493] font-medium">Click to upload poster</p>
                    <p className="text-sm text-gray-400 mt-2">Supports: Image files</p>
                  </div>
                  <input
                    type="file"
                    id="posterImage"
                    onChange={onChange}
                    accept="image/*"
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>

          {/* YouTube URL */}
          <div>
            <label htmlFor="youtubeUrl" className="block text-lg font-medium text-[#FF1493] mb-2">
              YouTube URL
            </label>
            <input
              type="url"
              id="youtubeUrl"
              value={youtubeUrl}
              onChange={onChange}
              placeholder="e.g., https://www.youtube.com/watch?v=xyz"
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-lg font-medium text-[#FF1493] mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={onChange}
              placeholder="e.g., The Matrix"
              minLength={2}
              maxLength={100}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Release Year */}
          <div>
            <label htmlFor="releaseYear" className="block text-lg font-medium text-[#FF1493] mb-2">
              Release Year
            </label>
            <input
              type="number"
              id="releaseYear"
              value={releaseYear}
              onChange={onChange}
              placeholder="e.g., 1999"
              min={1900}
              max={new Date().getFullYear()}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Duration */}
          <div>
            <label htmlFor="duration" className="block text-lg font-medium text-[#FF1493] mb-2">
              Duration (in minutes)
            </label>
            <input
              type="number"
              id="duration"
              value={duration}
              onChange={onChange}
              placeholder="e.g., 136"
              min={1}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-lg font-medium text-[#FF1493] mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={onChange}
              placeholder="Enter movie description..."
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
              rows="4"
            />
          </div>

          {/* Language */}
          <div>
            <label htmlFor="language" className="block text-lg font-medium text-[#FF1493] mb-2">
              Language
            </label>
            <input
              type="text"
              id="language"
              value={language}
              onChange={onChange}
              placeholder="e.g., English"
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Country of Origin */}
          <div>
            <label htmlFor="country" className="block text-lg font-medium text-[#FF1493] mb-2">
              Country of Origin
            </label>
            <input
              type="text"
              id="country"
              value={country}
              onChange={onChange}
              placeholder="e.g., USA"
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Genre Dropdown */}
          <div>
            <label htmlFor="genre" className="block text-lg font-medium text-[#FF1493] mb-2">
              Genre
            </label>
            <select
              id="genre"
              value={genre}
              onChange={onChange}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white"
            >
              <option value="">Select a genre</option>
              {genreOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Content Rating */}
          <div>
            <label htmlFor="contentRating" className="block text-lg font-medium text-[#FF1493] mb-2">
              Content Rating
            </label>
            <input
              type="text"
              id="contentRating"
              value={contentRating}
              onChange={onChange}
              placeholder="e.g., PG-13"
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Trailer URL (Optional) */}
          <div>
            <label htmlFor="trailerUrl" className="block text-lg font-medium text-[#FF1493] mb-2">
              Trailer URL (Optional)
            </label>
            <input
              type="url"
              id="trailerUrl"
              value={trailerUrl}
              onChange={onChange}
              placeholder="e.g., https://www.youtube.com/watch?v=abc"
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Is Featured Checkbox */}
          <div className="space-y-2">
            <label className="block text-lg font-medium text-[#FF1493] mb-2">
              Featured Status
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isFeatured"
                checked={isFeatured}
                onChange={onChange}
                className="h-5 w-5 text-[#FF1493] border-gray-600 focus:ring-[#FF8C00]"
              />
              <span className="text-white">Is this movie featured?</span>
            </label>
          </div>

          {/* Status Dropdown */}
          <div>
            <label htmlFor="status" className="block text-lg font-medium text-[#FF1493] mb-2">
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={onChange}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white"
            >
              <option value="">Select status</option>
              {statusOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Resolution */}
          <div>
            <label htmlFor="resolution" className="block text-lg font-medium text-[#FF1493] mb-2">
              Resolution
            </label>
            <input
              type="text"
              id="resolution"
              value={resolution}
              onChange={onChange}
              placeholder="e.g., 1080p"
              required
              className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
            />
          </div>

          {/* Crew Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-[#FF1493] mb-6">Crew Information (Optional)</h2>

            {/* Main Actor Image */}
            <div className="border-2 border-dashed border-[#FF1493] rounded-xl p-4 text-center mb-8">
              {actorImagePreview ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={actorImagePreview} 
                    alt="Actor Preview" 
                    className="max-h-64 mx-auto rounded-lg mb-4"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setActorImagePreview(null);
                      setFormData(prev => ({ ...prev, mainActorImage: null }));
                    }}
                    className="text-[#FF1493] hover:text-[#FF8C00] text-sm"
                  >
                    Change Actor Image
                  </button>
                </div>
              ) : (
                <div className="py-12">
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-[#FF1493] mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-lg text-[#FF1493] font-medium">Click to upload main actor image</p>
                      <p className="text-sm text-gray-400 mt-2">Supports: Image files</p>
                    </div>
                    <input
                      type="file"
                      id="mainActorImage"
                      onChange={onChange}
                      accept="image/*"
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Main Actor Name */}
            <div className="mb-8">
              <label htmlFor="mainActorName" className="block text-lg font-medium text-[#FF1493] mb-2">
                Main Actor Name
              </label>
              <input
                type="text"
                id="mainActorName"
                value={mainActorName}
                onChange={onChange}
                placeholder="e.g., Keanu Reeves"
                className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>

            {/* Directors */}
            <div className="mb-8">
              <label htmlFor="directors" className="block text-lg font-medium text-[#FF1493] mb-2">
                Directors
              </label>
              <input
                type="text"
                id="directors"
                value={directors}
                onChange={onChange}
                placeholder="e.g., Lana Wachowski, Lilly Wachowski"
                className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>

            {/* Cast */}
            <div className="mb-8">
              <label htmlFor="cast" className="block text-lg font-medium text-[#FF1493] mb-2">
                Cast
              </label>
              <textarea
                id="cast"
                value={cast}
                onChange={onChange}
                placeholder="e.g., Keanu Reeves, Laurence Fishburne, Carrie-Anne Moss"
                className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
                rows="3"
              />
            </div>

            {/* Writers */}
            <div className="mb-8">
              <label htmlFor="writers" className="block text-lg font-medium text-[#FF1493] mb-2">
                Writers
              </label>
              <input
                type="text"
                id="writers"
                value={writers}
                onChange={onChange}
                placeholder="e.g., Lana Wachowski, Lilly Wachowski"
                className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>

            {/* Producers */}
            <div className="mb-8">
              <label htmlFor="producers" className="block text-lg font-medium text-[#FF1493] mb-2">
                Producers
              </label>
              <input
                type="text"
                id="producers"
                value={producers}
                onChange={onChange}
                placeholder="e.g., Joel Silver"
                className="w-full px-4 py-3 bg-gray-800 border border-[#FF1493] rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={(!posterUrl && !formData.posterImage) || !youtubeUrl || !title || !releaseYear || !duration || !genre || !status}
            className={`w-full py-3 px-4 rounded-xl font-bold text-lg transition-all duration-300 ${
              (posterUrl || formData.posterImage) && youtubeUrl && title && releaseYear && duration && genre && status
                ? "bg-gradient-to-r from-[#FF1493] via-[#FF8C00] to-[#FFD700] hover:from-[#FF8C00] hover:to-[#FF1493] shadow-lg hover:shadow-[#FF8C00]/30"
                : "bg-gray-700 text-gray-500 cursor-not-allowed"
            }`}
          >
            Update Movie
          </button>
        </form>
      </div>
    </main>
  );
}